
import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Report } from "@/types/report";
import { useToast } from "@/hooks/use-toast";

const ITEMS_PER_PAGE = 10;

export const useAdminReports = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [showDeductedOnly, setShowDeductedOnly] = useState(false);
  const [showOtherSubjectsOnly, setShowOtherSubjectsOnly] = useState(false);
  const { toast } = useToast();

  const { data: reports = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin-reports', currentPage, search, showDeductedOnly, showOtherSubjectsOnly],
    queryFn: async () => {
      let supabaseQuery = supabase
        .from('student_development_reports')
        .select('id, created_at, student_nickname, student_full_name, subject, teaching_date, status, start_date, end_date, teaching_time_slot, teacher_display, submitter_email, learning_status, submitted_on_time, rejection_reason, hours_deducted, created_by_admin, teaching_content, other_subject', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (search.trim()) {
        const searchTerm = search.trim();
        supabaseQuery = supabaseQuery.or(
          [
            `student_nickname.ilike.%${searchTerm}%`,
            `student_full_name.ilike.%${searchTerm}%`,
            `teacher_display.ilike.%${searchTerm}%`,
            `subject.ilike.%${searchTerm}%`,
            `submitter_email.ilike.%${searchTerm}%`,
            `teacher_id.ilike.%${searchTerm}%`
          ].join(',')
        );
      }

      if (showDeductedOnly) {
        supabaseQuery = supabaseQuery.eq('hours_deducted', false);
      }

      if (showOtherSubjectsOnly) {
        supabaseQuery = supabaseQuery.eq('other_subject', true);
      }

      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;
      const { data, error, count } = await supabaseQuery.range(from, to);

      if (error) throw error;
      setTotalPages(Math.max(1, Math.ceil((count || 0) / ITEMS_PER_PAGE)));

      return (Array.isArray(data) ? data.filter(item =>
        typeof item === 'object' &&
        item !== null &&
        'id' in item
      ) : []) as Report[];
    }
  });

  const onSearch = useCallback(() => {
    setCurrentPage(1);
    setSearch(pendingSearch.trim());
  }, [pendingSearch]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleReportUpdate = () => {
    refetch();
    toast({
      title: 'รายงานถูกอัพเดทแล้ว',
      description: 'ข้อมูลในรายการรายงานได้รับการอัพเดท',
    });
  };

  const setSearchInput = (value: string) => {
    setPendingSearch(value);
  };

  return {
    reports,
    isLoading: isLoading || isFetching,
    currentPage,
    totalPages,
    handlePageChange,
    handleReportUpdate,
    refetch,
    search: pendingSearch,
    setSearch: setSearchInput,
    onSearch,
    showDeductedOnly,
    setShowDeductedOnly,
    showOtherSubjectsOnly,
    setShowOtherSubjectsOnly,
  };
};
